import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '..';
import { updateLicenses } from '../account.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;

export const useAdminUserStore = defineStore('admin-user', {
    state: () => ({
        Users:[],
        Referrers: [],
        commission: 0,
        loading: false
    }),
    actions: {
        async loadUsers(data) {
            const alertStore = useAlertStore();
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/admin/load-users`, data);
                if(data.from === 0 || !data.from)
                    this.Users = res.totalUsers
                else this.Users.push(...res.totalUsers);
                this.loading = false;
            } catch (error) {
                this.loading = false;
                alertStore.error(error);
            }
        },
        // reseller payout address (Reseller tab): the full address is only loaded when Show is clicked
        async getResellerPayout(user_id) {
            try { return (await fetchWrapper.get(`${baseUrl}/admin/reseller/payout?user_id=${encodeURIComponent(user_id)}`)).payout || null; }
            catch (error) { useAlertStore().error(error); return undefined; }
        },
        async saveResellerPayout(payload) {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/reseller/payout`, payload); useAlertStore().success(res.payout ? 'Payout address saved' : 'Payout address removed'); return res; }
            catch (error) { useAlertStore().error(error); return null; }
        },
        async loadResellerUsers() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/admin/load-reseller-users`);
                this.Users = res.totalUsers;
            } catch (error) {
                alertStore.error(error);
            }
        },
        async loadReferrals(data) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/load-referrals`, data);
                this.Users = res.users;
                this.Referrers = res.referrers;
            } catch (error) {
                alertStore.error(error);
            }
        },
        async loadReferrerUsers() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/admin/load-referrer-users`);
                this.Users = res.totalUsers;
                this.commission = res.commission;
            } catch (error) {
                alertStore.error(error);
            }
        },
        async saveCommission(data) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/savecommission`, {data});
                this.commission = res.commission;
                alertStore.success('Updated referral commission')
            } catch (error) {
                alertStore.error(error);
            }
        },
        async saveSiteInfo(data) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/savemeta`, {title: data.title, description: data.description});
                alertStore.success('Updated site information')
            } catch (error) {
                alertStore.error(error);
            }
        },
        async saveNotification(data) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/savenotification`, {notification: data.notification});
                alertStore.success('Updated site information')
            } catch (error) {
                alertStore.error(error);
            }
        },
        async loadRegistrationSettings() {
            const alertStore = useAlertStore();
            try {
                return await fetchWrapper.get(`${baseUrl}/admin/registration-settings`);
            } catch (error) {
                alertStore.error(error);
            }
        },
        async saveRegistrationSettings(data) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/registration-settings`, {
                    registrationDisabled: data.registrationDisabled
                });
                alertStore.success('Updated registration settings')
                return res;
            } catch (error) {
                alertStore.error(error);
            }
        },
        async createUser(createRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/create`,{createRequest});
                alertStore.success('Created New User')
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        async addResellerUser(createRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/add-reseller`,{createRequest});
                alertStore.success('Add Reseller')
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        async updateUser(updateRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/update`,{updateRequest});
                alertStore.success('Updated User')
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        async updateUserBalance(updateRequest) {
            const alertStore = useAlertStore();
            try {
                const result = await fetchWrapper.post(`${baseUrl}/admin/user/update-balance`,{updateRequest});
                this.Users = this.Users.map(item => {
                    if(item.uuid === result.result.uuid) {
                        return { ...item, ...result.result }   // keep fields the reply lacks (e.g. payout on the Reseller tab)
                    }
                    return item
                })
                alertStore.success('Added Balance')
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        async refundUser(updateRequest) {
            const alertStore = useAlertStore();
            try {
                const result = await fetchWrapper.post(`${baseUrl}/admin/user/refund-balance`,{updateRequest});
                this.Users = this.Users.map(item => {
                    if(item.uuid === result.result.uuid) {
                        return { ...item, ...result.result }   // keep fields the reply lacks (e.g. payout on the Reseller tab)
                    }
                    return item
                })
                if(updateRequest.bType === 'TOPUP') alertStore.success('Topup Balance')
                else alertStore.success('Refund Balance')
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        // the customer's active proxies, for the export file (audited on the server)
        async exportProxies(user_id, format) {
            try {
                return await fetchWrapper.post(`${baseUrl}/admin/user/export-proxies`, { user_id, format });
            } catch (error) {
                useAlertStore().error(error);
                return null;
            }
        },
        async logoutUser(user_id) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/logout`, { user_id });
                alertStore.success("Logged out: they must sign in again");
            } catch (error) {
                alertStore.error(error);
            }
        },
        async deleteUsers(uuids) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/delete`, { uuids });
                updateLicenses()
                alertStore.success("Delete successful")
                
            } catch (error) {
                alertStore.error(error);
            }
        },
        async deleteResellers(uuids) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/user/delete-reseller`, { uuids });
                updateLicenses()
                alertStore.success("Delete successful")
                
            } catch (error) {
                alertStore.error(error);
            }
        },
    },
});
